import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Dropdown, Form, Modal, Row } from "react-bootstrap";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import {
  type Case,
  type Client,
  type DocumentDetail,
  DocumentSubType,
  ExternalPartners,
  type Party,
  Role,
  ToggleableFeature,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import Help from "@/components/Help";
import { Modal as BPModal } from "@/components/Modals";
import {
  useCaseApi,
  useClientApi,
  useDocumentApi,
  useFirmApi,
  useTagApi,
} from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import useDocuments from "@/hooks/useDocuments";
import type {
  CaseResponse,
  ClientResponse,
  DocumentClient,
  UserResponse,
  VenueResponse,
} from "@/lib/types";
import {
  addUploadDate,
  getCaseJurisdiction,
  getSubTypeText,
  hasCanceledSubscription,
  isInTrialOrHasActiveSubscription,
  leadAttorneyName,
  sanitizeFiles,
  userCanUseFeature,
} from "@/lib/utils";
import "./styles.scss";
import "./case.scss";
import rg4js from "raygun4js";
import CaseDiscovery from "@/components/CaseDiscovery";
import AddEditCaseModal from "@/components/CaseManagement/AddEditCaseModal";
import CaseProduction from "@/components/CaseProduction";
import CaseTabs from "@/components/CaseTabs/CaseTabs";
import GeneralInformationScreen from "@/components/ConfirmInfo/GeneralInformation";
import ExternalFileBrowserSelect from "@/components/ExternalPartner/ExternalFileBrowserSelect";
import ExternalPartnerConnection from "@/components/ExternalPartner/ExternalPartnerConnection";
import ExternalPartnerMenu from "@/components/ExternalPartner/ExternalPartnerMenu";
import LeavingGuard from "@/components/LeavingGuard";
import Loading from "@/components/Loading";
import SignUpModal from "@/components/Modals/SignUpModal";
import { TagsList } from "@/components/TagsList";
import useFirm from "@/hooks/useFirm";
import ClioLogo from "@/images/Glyph_Clio Glyph Blue.svg?react";
import BriefpointLogo from "@/images/logo.svg?react";
import MyCaseLogo from "@/images/MyCase.svg?react";
import SmokeballLogo from "@/images/Smokeball_logo_mark.svg?react";
import { CaliforniaId } from "@/lib/constants";
import {
  getFirmCasesOptions,
  getProcessingDocumentsOptions,
  getUnarchivedCaseDocumentListOptions,
} from "@/lib/query-options";
import type { CaseTab } from "@/lib/types";
import {
  caseTypeDisplay,
  getUploadingDocument,
  toLocaleUSDateString,
} from "@/lib/utils";
import RequiredCaseInfo from "@/screens/DocumentGeneration/RequiredCaseInfo";
import SelectPartyModal from "@/screens/DocumentGeneration/SelectPartyModal";
import { filterTagsForParties } from "@/utils/Requests/utils";

export const partySpecificTags = [
  "Business",
  "Care Provider",
  "Debtor",
  "Driver",
  "Employer",
  "Government",
  "Insurance Company",
  "Landlord",
  "Manufacturer",
  "Owner",
  "Professional",
  "Tenant",
  "Officer",
  "Expert",
  "Witness",
  "Third Party",
  "Bank",
  "Broker",
  "Real Estate Agent",
];

type CaseParams = {
  id: string;
};

export default function CaseOverviewPage() {
  const { user, firm } = useAuth();
  if (!user) throw new Error("User is undefined");
  if (!firm) throw new Error("Firm is undefined");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation<{ case: CaseResponse }>();
  const { id } = useParams<CaseParams>();
  const caseApi = useCaseApi();
  const clientApi = useClientApi();
  const documentApi = useDocumentApi();
  const [users, , , ,] = useFirm(firm?.id, false);
  const [uploadingDocuments, setUploadingDocuments] = useState<
    DocumentClient[]
  >([]);
  const [isSignUpModalShow, setIsSignUpModalShow] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [_, uploadFiles] = useDocuments(true, id);
  const firmApi = useFirmApi();
  const casesQuery = useQuery({
    ...getFirmCasesOptions(firmApi, user?.firmId ?? ""),
  });

  const documentsQuery = useQuery({
    ...getUnarchivedCaseDocumentListOptions(documentApi, id ?? ""),
    select: (data) => {
      return data.map((document) => addUploadDate(document as DocumentDetail));
    },
  });

  const [, setError] = useState("");
  const queryClient = useQueryClient();
  const [_case, setCase] = useState<Case | undefined>(location.state?.case);
  const [showEditCase, setShowEditCase] = useState(false);
  const { jurisdictions } = useAuth();
  const tagApi = useTagApi();
  const history = useHistory();
  const [tags, setTags] = useState<Map<number, string> | undefined>();
  const [typeToGenerate, setTypeToGenerate] = useState<
    DocumentSubType | undefined
  >(
    Number(new URLSearchParams(location.search).get("generate")) as
      | DocumentSubType
      | undefined,
  );
  const [showCloseCase, setShowCloseCase] = useState(false);
  const [isClosingCase, setIsClosingCase] = useState(false);
  const [showUploadDocs, setShowUploadDocs] = useState(false);
  const [deleteDocuments, setDeleteDocuments] = useState(false);
  const pendingDraftType = useRef<DocumentSubType>(DocumentSubType.Unknown);
  const [isExternalUploading, setIsExternalUploading] =
    useState<boolean>(false);
  const [clients, setClients] = useState<ClientResponse[] | undefined>();

  const userCanUseDocHandling = userCanUseFeature(
    user,
    ToggleableFeature.Autodoc,
  );
  const selectedTab =
    (new URLSearchParams(location.search).get("tab") as CaseTab | undefined) ??
    "discovery";

  const jurisdiction = useMemo(() => {
    if (!jurisdictions || !firm) {
      return undefined;
    }

    const firmJx =
      firm.offices?.find((x) => x.jurisdictions?.length)?.jurisdictions?.[0]
        ?.jurisdictionId ?? CaliforniaId;
    const jx =
      jurisdictions?.find((j) => j.id === _case?.jurisdiction) ??
      jurisdictions?.find((j) => j.id === firmJx);
    return jx;
  }, [jurisdictions, firm, _case?.jurisdiction]);

  const venue = (_case?.venue &&
    jurisdiction?.venues?.find((x) => x.id === _case.venue)) as
    | VenueResponse
    | undefined;
  const nonArchivedDocs = documentsQuery.data?.filter((x) => !x.isArchived);
  const partyTags = useRef(new Map<number, string>());
  const hasProvidedInfo =
    user?.firstName &&
    user?.lastName &&
    user?.barNumber &&
    (!user?.role?.includes(Role.FirmAdmin) ||
      (firm?.name && firm?.address?.street && firm?.address.postalCode));

  const missingRequiredInfo = useMemo(() => {
    return !_case?.caseType || !_case?.clientPosition;
  }, [_case]);

  function toDateOrMin(val?: string | null) {
    return val ? new Date(val) : new Date(-8640000000000000);
  }

  useEffect(() => {
    let tagsLoaded = false;
    async function loadTags() {
      const tagsList = new Map<number, string>();
      const loadedTags = await tagApi.tagFindTags({});
      for (const t of loadedTags) {
        tagsList.set(t.id, t.name);
        if (t.ownerId || partySpecificTags.find((x) => x === t.name)) {
          partyTags.current.set(t.id, t.name);
        }
      }
      setTags(tagsList);
    }
    if (!tagsLoaded) {
      loadTags();
      return () => {
        tagsLoaded = true;
      };
    }
  }, [tagApi]);

  const loadCase = useCallback(
    async (user: UserResponse | undefined, _case: Case | undefined) => {
      if (user?.firmId && !_case) {
        const c = (await caseApi.caseGet({
          firmId: user.firmId,
          id,
        })) as CaseResponse;
        setCase(c);
      }
    },
    [caseApi, id],
  );

  useEffect(() => {
    loadCase(user, _case);
  }, [user, loadCase, _case]);

  const loadClients = useCallback(
    async (firmId: string) => {
      const c = (await clientApi.clientGetAll({
        firmId: firmId,
      })) as ClientResponse[];
      setClients(c);
    },
    [clientApi],
  );

  const hasValidSubscription = !hasCanceledSubscription(firm);

  const uploadFilesMutation = useMutation({
    mutationFn: async ({
      files,
    }: {
      files: File[];
      newUploadingDocs: DocumentClient[];
    }) => {
      return await uploadFiles(files, id);
    },
    retry: 1,
    retryDelay: 1000,
    onError: (error) => {
      setError(error.message || "Failed to upload files");
    },
    onSettled: (_data, _error, uploadData) => {
      setUploadingDocuments([
        ...uploadingDocuments.filter(
          (doc) => !uploadData.newUploadingDocs.includes(doc),
        ),
      ]);
      queryClient.invalidateQueries({
        ...getUnarchivedCaseDocumentListOptions(documentApi, id ?? ""),
      });
    },
  });

  useEffect(() => {
    if (!clients && firm && firm.id) {
      loadClients(firm.id);
    }
  }, [clients, firm, loadClients]);

  function handleChooseFile(e: React.MouseEvent) {
    e.preventDefault();
    if (!hasValidSubscription) {
      setIsSignUpModalShow(true);
      return;
    }
    fileInputRef?.current?.click();
  }

  async function handleUpload(fileList: FileList) {
    setError("");
    const files = Array.from(fileList);
    if (files.length) {
      const sanitizedFiles = sanitizeFiles(files);
      if (sanitizedFiles.length) {
        const newUploadingDocs = sanitizedFiles.map((file) =>
          getUploadingDocument(file.name),
        );
        setUploadingDocuments([...uploadingDocuments, ...newUploadingDocs]);

        uploadFilesMutation.mutate({ files: sanitizedFiles, newUploadingDocs });
      } else {
        setError("Only .pdf files are currently supported.");
      }
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { files } = e.target;
    if (files) {
      handleUpload(files);
    }
    e.target.value = "";
  }

  async function saveCase(_case: Case) {
    if (!_case.id) throw new Error("Case ID is required");
    await caseApi.casePut({
      firmId: user?.firmId || "",
      id: _case.id,
      _case,
    });

    setCase(_case);
    setShowEditCase(false);
  }

  function sortDocumentDates(
    first: DocumentClient,
    second: DocumentClient,
    sortColumn: string,
    sortDirectionAscending: boolean,
  ) {
    // I couldn't get realDueDate as a computed property to work, sorry
    const a = (
      (sortColumn === "realDueDate"
        ? toDateOrMin(first.info?.dueDateOverridden || first.info?.dueDate)
        : (first[sortColumn as keyof typeof first] as Date)) ??
      new Date(-8640000000000000)
    ).getTime();
    const b = (
      (sortColumn === "realDueDate"
        ? toDateOrMin(second.info?.dueDateOverridden || second.info?.dueDate)
        : (second[sortColumn as keyof typeof second] as Date)) ??
      new Date(-8640000000000000)
    ).getTime();

    return sortDirectionAscending ? a - b : b - a;
  }

  function nextDueDate(docs: DocumentClient[]) {
    const next = docs
      ?.sort((a, b) => sortDocumentDates(a, b, "realDueDate", true))
      ?.find(
        (x) =>
          toDateOrMin(x.info?.dueDateOverridden || x.info?.dueDate) >
          new Date(),
      );

    const dueDate = next?.info?.dueDateOverridden || next?.info?.dueDate;
    return dueDate ? toLocaleUSDateString(new Date(dueDate)) : "-";
  }

  function handleSelectGenerate(value: DocumentSubType): void {
    pendingDraftType.current = DocumentSubType.Unknown;
    // show info page if user has not provided info yet, else generate word document
    if (hasProvidedInfo) {
      if (!user?.isInactive && firm && isInTrialOrHasActiveSubscription(firm)) {
        setTypeToGenerate(value);
      } else {
        showSignupPage(value);
      }
    } else {
      pendingDraftType.current = value;
      setShowInfoModal(true);
    }
  }

  function showSignupPage(value: DocumentSubType) {
    rg4js("trackEvent", {
      type: "pageView",
      path: "/signup-modal",
    });
    pendingDraftType.current = value;
    setIsSignUpModalShow(true);
  }

  async function handleRequiredCase(_case: Case) {
    if (!_case.id) throw new Error("Case ID is required");

    await caseApi.casePut({
      firmId: user?.firmId || "",
      id: _case.id,
      _case,
    });

    setCase(_case);
  }

  async function handleChosePartyAndClient(
    party: Party,
    client?: Client,
  ): Promise<void> {
    if (!_case) {
      return;
    }
    setTypeToGenerate(undefined);
    const caseToPush = _case;
    if (!caseToPush.otherParties) {
      caseToPush.otherParties = [];
    }

    const selected = caseToPush.otherParties.findIndex(
      (p) => p.id === party.id,
    );

    if (selected === -1) {
      caseToPush.otherParties.push(party);
    } else {
      caseToPush.otherParties[selected] = party;
    }
    const previous = nonArchivedDocs?.filter(
      (x) =>
        x.info?.otherPartyId === party.id &&
        x.info?.documentSubType === typeToGenerate,
    );

    previous?.sort(
      (a, b) => (a.info?.setNumber ?? 1) - (b.info?.setNumber ?? 1),
    );
    const highest = previous?.pop();
    const start =
      (highest?.info?.startingNumber ?? 1) +
      (highest?.interrogatories?.length ??
        highest?.info?.interrogatoryCount ??
        0);

    // TODO: refactor GenerateDiscoveryRequestPage to use Tanstack Query and remove location.state.
    // Instead, pass the client.id and case.id as url params.
    history.push({
      pathname: `/clients/${client?.id}/cases/${_case.id}/generateDiscoveryRequest`,
      search: `?type=${typeToGenerate}&partyId=${party.id}&set=${(highest?.info?.setNumber ?? 0) + 1}&start=${start}`,
      state: { case: caseToPush, client: client },
    });
  }

  async function onCaseCloseClose() {
    setShowCloseCase(false);
    setDeleteDocuments(false);
    setIsClosingCase(false);
  }

  async function handleCaseClose() {
    setIsClosingCase(true);
    try {
      await caseApi.caseDelete({
        firmId: user?.firmId || "",
        id: _case?.id ?? "",
        deleteDocumentData: deleteDocuments,
      });

      history.push("/cases");
    } finally {
      setIsClosingCase(false);
    }
  }

  function refetchDocumentList() {
    const caseDocumentListQueryKey = getUnarchivedCaseDocumentListOptions(
      documentApi,
      id ?? "",
    ).queryKey;

    queryClient.invalidateQueries({ queryKey: caseDocumentListQueryKey });
    queryClient.refetchQueries({ queryKey: caseDocumentListQueryKey });
    queryClient.invalidateQueries(getProcessingDocumentsOptions(documentApi));
    queryClient.refetchQueries(getProcessingDocumentsOptions(documentApi));
  }

  function externalUploadRefresh() {
    setShowUploadDocs(false);
    refetchDocumentList();
  }

  function getCaseSourceLogo(partner?: ExternalPartners) {
    if (!partner || partner === ExternalPartners.None) {
      return <BriefpointLogo title="Briefpoint Case" />;
    }

    switch (partner) {
      case ExternalPartners.Clio:
        return <ClioLogo title="Clio Case" />;
      case ExternalPartners.Smokeball:
        return <SmokeballLogo title="Smokeball Case" />;
      case ExternalPartners.MyCase:
        return <MyCaseLogo title="MyCase Case" />;
      default:
        return <BriefpointLogo title="Briefpoint Case" />;
    }
  }

  function setSelectedTab(tab: CaseTab) {
    history.push({
      search: `?tab=${tab}`,
    });
  }

  return (
    <>
      <LeavingGuard
        when={uploadingDocuments.length > 0}
        message="Your upload will stop if you leave this page."
        title="Upload in progress!"
      />
      <Container className="page-container-multi">
        <Loading
          isLoading={
            documentsQuery.isLoading ||
            (_case === undefined &&
              clients === undefined &&
              jurisdictions === undefined &&
              users === undefined)
          }
        >
          <Row key="heading" style={{ marginTop: "24px" }}>
            <Col>
              <div className="bread-crumbs mb-4">
                <Link to={"/cases"}>Cases</Link> {`> ${_case?.shortTitle}`}
              </div>
            </Col>
            <Col className="actions-cell" />
          </Row>
          <Row
            key="class-details-wrapper-multi"
            className="class-details-wrapper-multi"
          >
            <Row className="case-details-title-wrapper">
              <Col className="case-details-title">
                {!!firm?.preferences?.enabledExternalProviders?.size && (
                  <span className="sourceLogo">
                    {getCaseSourceLogo(_case?.integration?.partner)}
                  </span>
                )}
                {_case?.shortTitle || _case?.title}
              </Col>
            </Row>
            <Row className="case-details">
              <Col className="col-9">
                <Row key="case-headers-main" className="case-headers">
                  <Col className="col-2">Case Type</Col>
                  <Col className="col-3">Tags</Col>
                  <Col>Case Number</Col>
                  <Col>Venue</Col>
                  <Col>Lead Attorney</Col>
                  {!!jurisdiction?.supportsDueDates && <Col>Next Due Date</Col>}
                </Row>
                <Row key="case-details-main">
                  <Col className="col-2">
                    {caseTypeDisplay(_case?.caseType, jurisdiction, true, "-")}
                  </Col>
                  <Col className="col-3">
                    <TagsList
                      tagIds={_case?.tagIds}
                      availableTags={tags}
                      questionId={_case?.id?.toString() ?? ""}
                      startTrimmingTagsAfter={3}
                    />
                  </Col>
                  <Col>{_case?.caseNumber ?? "-"}</Col>
                  <Col title={venue?.shortName ?? ""}>{venue?.shortName}</Col>
                  <Col>{users && leadAttorneyName(_case, users)}</Col>
                  {!!jurisdiction?.supportsDueDates && (
                    <Col>{nextDueDate(documentsQuery.data ?? [])}</Col>
                  )}
                </Row>
              </Col>
              <Col className="actions-cell col-3">
                <div className="case-actions-col">
                  <Button
                    data-testid="close-case-button"
                    variant="link"
                    style={{ marginRight: "8px" }}
                    className="close-case-btn"
                    title="Close Case"
                    onClick={() => setShowCloseCase(true)}
                  >
                    Close Case
                  </Button>
                  <Button
                    variant="outline"
                    className="btn-26 edit-case-btn"
                    onClick={() => setShowEditCase(true)}
                  >
                    Edit Case
                  </Button>
                </div>
              </Col>
            </Row>
          </Row>
          {selectedTab === "discovery" && (
            <Row key="actions" className="table-actions-row">
              {_case?.integration?.identifier && (
                <Col className="col-auto">
                  <ExternalPartnerConnection />
                </Col>
              )}

              <Col className="col-auto">
                <Dropdown>
                  <Dropdown.Toggle
                    as={Button}
                    variant="outline"
                    size="sm"
                    id="draft-dropdown"
                  >
                    Draft Request (Beta)
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() =>
                        handleSelectGenerate(
                          DocumentSubType.RequestsForAdmission,
                        )
                      }
                    >
                      {
                        getSubTypeText(
                          DocumentSubType.RequestsForAdmission,
                          jurisdiction?.id ?? "",
                        ).singular
                      }
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        handleSelectGenerate(
                          DocumentSubType.RequestsForProduction,
                        )
                      }
                    >
                      {
                        getSubTypeText(
                          DocumentSubType.RequestsForProduction,
                          jurisdiction?.id ?? "",
                        ).singular
                      }
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        handleSelectGenerate(
                          DocumentSubType.SpecialInterrogatories,
                        )
                      }
                    >
                      {
                        getSubTypeText(
                          DocumentSubType.SpecialInterrogatories,
                          jurisdiction?.id ?? "",
                        ).plural
                      }
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col className="d-flex col-auto gap-2">
                {user?.externalConnection?.isActive ? (
                  <ExternalPartnerMenu
                    _case={_case}
                    externalAction={setShowUploadDocs}
                    bpDefault={handleChooseFile}
                    isUploading={uploadFilesMutation.isPending}
                  />
                ) : (
                  <Button
                    data-testid="upload-document-button"
                    variant="default"
                    size="sm"
                    onClick={handleChooseFile}
                    disabled={uploadFilesMutation.isPending}
                  >
                    {uploadFilesMutation.isPending ? "Uploading..." : "Respond"}
                  </Button>
                )}
                <Help
                  id="document-help"
                  text={`Briefpoint currently accepts the following state court documents: <ul><li class="tooltip-row">${getSubTypeText(DocumentSubType.RequestsForAdmission, getCaseJurisdiction(casesQuery.data, _case?.id ?? "") ?? "").plural}</li><li class="tooltip-row">${getSubTypeText(DocumentSubType.RequestsForProduction, getCaseJurisdiction(casesQuery.data, _case?.id ?? "") ?? "").plural}</li><li class="tooltip-row">${getSubTypeText(DocumentSubType.SpecialInterrogatories, getCaseJurisdiction(casesQuery.data, _case?.id ?? "") ?? "").plural}</li>${getCaseJurisdiction(casesQuery.data, _case?.id ?? "") === CaliforniaId ? `<li class="tooltip-row">${getSubTypeText(DocumentSubType.FormInterrogatories, getCaseJurisdiction(casesQuery.data, _case?.id ?? "") ?? "").plural}</li>` : ""}</ul>`}
                  direction={"bottom"}
                />
              </Col>

              <Col className="col-auto" />
            </Row>
          )}
          {userCanUseDocHandling && (
            <CaseTabs
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />
          )}
          {selectedTab === "production" && _case && (
            <CaseProduction case={_case} />
          )}
          {selectedTab === "discovery" && (
            <CaseDiscovery
              id={id}
              jurisdiction={jurisdiction}
              _case={_case}
              clients={clients}
              chooseFile={handleChooseFile}
              isUploading={uploadFilesMutation.isPending}
            />
          )}

          {_case && firm && (
            <AddEditCaseModal
              availableTags={tags}
              setAvailableTags={setTags}
              title="Edit Case"
              confirm="Save"
              isShow={showEditCase}
              onClose={() => setShowEditCase(false)}
              onConfirm={saveCase}
              users={users}
              jurisdictions={jurisdictions ?? []}
              _case={_case}
            />
          )}
          {_case && jurisdictions && missingRequiredInfo && (
            <RequiredCaseInfo
              show={!!typeToGenerate && missingRequiredInfo}
              onClose={() => setTypeToGenerate(undefined)}
              onConfirm={handleRequiredCase}
              _case={_case}
              jurisdictions={jurisdictions}
            />
          )}
          {_case && tags && jurisdiction && (
            <SelectPartyModal
              availableTags={filterTagsForParties(
                tags,
                _case,
                partyTags.current,
              )}
              show={!!typeToGenerate && !missingRequiredInfo}
              type={typeToGenerate}
              onClose={() => setTypeToGenerate(undefined)}
              onConfirm={handleChosePartyAndClient}
              jurisdiction={jurisdiction}
              _case={_case}
              clients={clients}
            />
          )}
          <SignUpModal
            isShow={isSignUpModalShow}
            onClose={(success) => {
              setIsSignUpModalShow(false);
              if (success && pendingDraftType.current) {
                handleSelectGenerate(pendingDraftType.current);
              }
            }}
          />

          {!hasProvidedInfo && (
            <Modal
              show={showInfoModal}
              onClose={() => setShowInfoModal(false)}
              size="xl"
              title={"Information for output document"}
              showCancelButton={false}
              showConfirmButton={false}
              preventBackgroundClick
            >
              <GeneralInformationScreen
                documentId={"anything"}
                onSave={async () => {
                  setShowInfoModal(false);
                  if (pendingDraftType.current) {
                    handleSelectGenerate(pendingDraftType.current);
                  }
                }}
              />
            </Modal>
          )}
        </Loading>
      </Container>
      <BPModal
        onClose={onCaseCloseClose}
        onConfirm={handleCaseClose}
        show={showCloseCase}
        title={<span className="fw-500">Close Case</span>}
        confirmText={`Close Case${deleteDocuments ? " and Delete Documents" : ""}`}
        cancelText={
          <Button variant="outline" onClick={onCaseCloseClose}>
            Cancel
          </Button>
        }
        confirming={isClosingCase}
        confirmingText="Closing..."
      >
        <div>
          <div className="modal-body-text mb-3">
            Please confirm you wish to close this Case, it will no longer show
            up in search results on your dashboard.
          </div>
          <Form.Check
            data-testid="delete-docs-checkbox"
            id="delete-docs"
            name="delete-docs"
            checked={deleteDocuments}
            onChange={(e) => setDeleteDocuments(e.target.checked)}
            label="Also delete all associated Documents."
            className="modal-body-text-checkbox"
          />
          {deleteDocuments && (
            <div className="modal-body-text mt-3 mb-3 text-danger">
              This will delete all Documents associated with this Case along
              with any content entered for those Documents: objections,
              responses, etc. <strong>This action cannot be undone.</strong>
            </div>
          )}
        </div>
      </BPModal>
      {_case?.integration?.identifier && (
        <Modal
          onClose={() => setShowUploadDocs(false)}
          className="externalModal"
          show={showUploadDocs}
          title="Upload Files"
          confirmText="Upload Files"
        >
          <ExternalFileBrowserSelect
            case={_case}
            setIsExternalUploading={setIsExternalUploading}
            isExternalUploading={isExternalUploading}
            onFinish={externalUploadRefresh}
            onUploadSuccess={refetchDocumentList}
          />
        </Modal>
      )}

      <input
        data-testid="document-file-input"
        type="file"
        id="file-input"
        name="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="d-none"
        multiple
      />
    </>
  );
}
